export default function SANContent() {
  return (
    <article className="py-24 bg-white">
      <div className="max-w-screen-xl mx-auto px-8 md:px-16">
        <div className="max-w-2xl mx-auto">

          {/* Intro */}
          <p
            className="text-[#4A4440] text-lg leading-[1.9] mb-10"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            There's a particular kind of beauty that only small apartments can achieve — an intimacy, a warmth, a sense that every object has been chosen with care. The challenge is getting there without the space feeling cluttered, dark, or visually noisy. Soft neutrals are the answer.
          </p>
          <p
            className="text-[#4A4440] text-base leading-[1.9] mb-16"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            A palette built from cream, oat, warm ivory, and sand doesn't just look calm — it actively makes a room feel larger. Light bounces between surfaces. Walls recede. Furniture blends rather than competes. The result is a space that feels considered and cohesive, even when it's compact.
          </p>

          {/* Section 1 */}
          <h2
            className="text-[#2C2825] text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            Why Soft Neutrals Work So Well in Small Spaces
          </h2>
          <p
            className="text-[#4A4440] text-base leading-[1.9] mb-6"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            Colour contrast creates visual weight. When walls, floors, furniture, and textiles are all fighting for attention in different tones, the eye has nowhere to rest — and the room feels smaller and busier than it actually is. Soft neutrals eliminate that friction.
          </p>
          <p
            className="text-[#4A4440] text-base leading-[1.9] mb-10"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            When your sofa is close in tone to your walls, and your rug echoes the floor, the room reads as one continuous, flowing space. Boundaries soften. The eye travels further. And the whole apartment feels more generous than its square footage suggests.
          </p>

          {/* Pull quote */}
          <blockquote className="border-l-2 border-[#C8BAA8] pl-8 my-14">
            <p
              className="text-[#2C2825] text-xl md:text-2xl leading-relaxed italic"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              "The goal isn't to make a small apartment look bigger. It's to make it feel better — calmer, more intentional, more like home."
            </p>
          </blockquote>

          {/* Image */}
          <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-16">
            <img
              src="https://public.readdy.ai/ai/img_res/eb509be0947314a221f996bba68e72e8.jpg"
              alt="Small apartment with soft neutral palette"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Section 2 */}
          <h2
            className="text-[#2C2825] text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            Building Your Neutral Palette
          </h2>
          <p
            className="text-[#4A4440] text-base leading-[1.9] mb-8"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            A successful neutral palette isn't just "everything beige." It's a carefully layered range of warm tones that sit harmoniously together. Think of it in three layers:
          </p>

          <div className="space-y-6 mb-14">
            {[
              {
                label: "Base Layer — Walls & Floor",
                text: "Start with warm white or soft cream walls. Avoid cool whites — they read as grey in low light and make a small space feel clinical. For floors, light oak or warm-toned wood is ideal. If you have dark floors, a large oat or ivory rug will lift the whole room.",
              },
              {
                label: "Mid Layer — Large Furniture",
                text: "Your sofa is the most important piece. Choose linen or boucle in oat, sand, or warm ivory. Avoid anything too dark or too saturated — it will anchor the room in a way that makes it feel heavier. A light wood coffee table keeps the floor visible and the space feeling open.",
              },
              {
                label: "Top Layer — Textiles & Accessories",
                text: "This is where you add depth and texture. Throw pillows in slightly different tones — a warm sand cushion next to an ivory boucle one — create visual interest without colour contrast. A linen throw draped over the sofa arm adds softness. A ceramic vase in a muted clay tone grounds the whole arrangement.",
              },
            ].map((item) => (
              <div key={item.label} className="flex gap-5">
                <div className="w-1 flex-shrink-0 bg-[#D4C5B0] rounded-full mt-1" />
                <div>
                  <p
                    className="text-[#2C2825] text-sm tracking-wide mb-2"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                  >
                    {item.label}
                  </p>
                  <p
                    className="text-[#4A4440] text-sm leading-[1.85]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                  >
                    {item.text}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Section 3 */}
          <h2
            className="text-[#2C2825] text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            Furniture Choices That Make a Small Room Feel Larger
          </h2>
          <p
            className="text-[#4A4440] text-base leading-[1.9] mb-8"
            style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
          >
            In a small apartment, every furniture decision matters more. Here's what to prioritise:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {[
              { title: "Legs Over Bases", body: "Furniture with visible legs — sofas, chairs, side tables — lets light pass underneath and makes the floor feel continuous. Avoid heavy block-base sofas that sit flush to the floor." },
              { title: "Round Coffee Tables", body: "A round table removes hard corners from the room, making it easier to move around and softer to look at. Light oak or travertine are ideal materials." },
              { title: "Multifunctional Pieces", body: "An ottoman with storage, a bench at the foot of a bed, a side table that doubles as a stool — in a small space, every piece should earn its place." },
              { title: "Fewer, Better Pieces", body: "Resist the urge to fill every corner. A small room with five well-chosen pieces feels more spacious than one with ten average ones. Edit ruthlessly." },
              { title: "Low-Profile Seating", body: "Lower sofas and chairs keep sightlines open and make ceilings feel higher. Avoid anything with a high back that cuts across the room visually." },
              { title: "Mirrors Strategically Placed", body: "A large mirror on a wall opposite a window doubles the light in the room and creates the illusion of depth. Keep the frame simple — thin metal or natural wood." },
            ].map((item) => (
              <div key={item.title} className="bg-[#FAF7F0] rounded-xl p-6">
                <p
                  className="text-[#2C2825] text-sm mb-2"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                >
                  {item.title}
                </p>
                <p
                  className="text-[#6B5E53] text-sm leading-[1.8]"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                >
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          {/* Image 2 */}
          <div className="w-full aspect-[16/9] rounded-xl overflow-hidden mb-16">
            <img
              src="https://readdy.ai/api/search-image?query=compact%20apartment%20living%20room%20round%20light%20oak%20coffee%20table%20low%20profile%20linen%20sofa%20cream%20walls%20warm%20sunlight%20minimal%20styling%20neutral%20palette%20editorial%20interior%20photography%20calm%20serene&width=1200&height=675&seq=san_content2&orientation=landscape"
              alt="Compact living room with round coffee table and low-profile sofa"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Section 4 — Styling Tips */}
          <h2
            className="text-[#2C2825] text-3xl md:text-4xl mb-6"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
          >
            7 Styling Tips for a Small Neutral Living Room
          </h2>

          <div className="space-y-8 mb-14">
            {[
              { num: "01", tip: "Keep the floor as clear as possible", detail: "Visible floor space is the single biggest factor in how large a room feels. Avoid rugs that are too small — they make the room feel fragmented. Go as large as you can." },
              { num: "02", tip: "Use texture to create depth", detail: "Without colour contrast, texture does the work. Mix linen, boucle, cotton, and natural wood to create a layered, interesting space that doesn't feel flat." },
              { num: "03", tip: "Let light in — all of it", detail: "Sheer linen curtains that pool slightly on the floor let in maximum light while adding softness. Avoid heavy drapes that block light and make walls feel closer." },
              { num: "04", tip: "Keep the palette warm, not cool", detail: "Warm neutrals (cream, oat, sand, warm ivory) feel more inviting and spacious than cool ones (grey, cool white, taupe). Even a slight warmth in the palette makes a room feel more alive." },
              { num: "05", tip: "Limit decorative objects", detail: "In a small space, less is genuinely more. Choose three or four objects you love — a ceramic vase, a stack of books, a single dried botanical — and give them space to breathe." },
              { num: "06", tip: "Use vertical space", detail: "Tall, slim shelving draws the eye upward and makes ceilings feel higher. Keep the top shelves lighter and more sparse — heavy objects at height make a room feel top-heavy." },
              { num: "07", tip: "Repeat tones across the room", detail: "If your sofa is oat, echo that tone in a cushion on the opposite chair, or in the rug. Repetition creates visual rhythm and makes a small space feel intentional rather than random." },
            ].map((item) => (
              <div key={item.num} className="flex gap-6">
                <span
                  className="text-[#C8BAA8] text-3xl leading-none flex-shrink-0 mt-1"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {item.num}
                </span>
                <div>
                  <p
                    className="text-[#2C2825] text-base mb-2"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                  >
                    {item.tip}
                  </p>
                  <p
                    className="text-[#6B5E53] text-sm leading-[1.85]"
                    style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                  >
                    {item.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </article>
  );
}
